import { useEffect, useRef, useState } from 'react';

const steps = [
  {
    num: '01',
    icon: 'ri-search-eye-line',
    title: 'Free Performance Assessment',
    description: 'We assess your operations, revenue strategy, contract structure, and asset position. No cost. No commitment. Just a clear picture of what\'s working and what isn\'t.',
  },
  {
    num: '02',
    icon: 'ri-file-text-line',
    title: 'Custom Performance Agreement',
    description: 'We build a fee structure tied to your KPIs. You choose the targets. We build the contract around them. No boilerplate. No surprises.',
  },
  {
    num: '03',
    icon: 'ri-bar-chart-grouped-line',
    title: 'Weekly Reporting. Real Results.',
    description: 'Your dedicated partner activates Day 1. Weekly dashboards, monthly strategy reviews, and direct access to your senior partner — every step of the way.',
  },
];

export default function HowItWorksSection() {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="py-24 bg-slate-950">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className={`mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center space-x-2 mb-6">
            <div className="h-px w-8 bg-amber-500"></div>
            <span className="text-xs font-medium text-amber-500 uppercase tracking-[0.25em]">How It Works</span>
          </div>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <h2 className="text-5xl lg:text-6xl font-light text-white leading-tight">
              From First Call to Full
              <br />
              <span className="text-amber-400 italic">Activation in 30 Days</span>
            </h2>
            <p className="text-base text-gray-400 font-light max-w-sm leading-relaxed">
              The next step is easy and low-commitment. Most owners see their blueprint within a week.
            </p>
          </div>
        </div>

        {/* Steps */}
        <div className="grid lg:grid-cols-3 gap-6 mb-16">
          {steps.map((step, index) => (
            <div
              key={index}
              className={`relative transition-all duration-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              <div className="bg-slate-900 rounded-2xl p-8 h-full border border-slate-800 hover:border-amber-500/30 transition-colors duration-300 group">
                {/* Step number + icon */}
                <div className="flex items-center justify-between mb-8">
                  <span className="text-6xl font-extralight text-amber-500/20 group-hover:text-amber-500/40 transition-colors duration-300">{step.num}</span>
                  <div className="w-12 h-12 flex items-center justify-center bg-amber-500/10 rounded-xl group-hover:bg-amber-500/20 transition-colors duration-300">
                    <i className={`${step.icon} text-2xl text-amber-400`}></i>
                  </div>
                </div>

                <h3 className="text-xl font-semibold text-white mb-3 leading-snug">{step.title}</h3>
                <p className="text-sm text-gray-400 font-light leading-relaxed">{step.description}</p>

                {/* Bottom accent */}
                <div className="mt-8 h-px w-0 bg-amber-500 group-hover:w-full transition-all duration-500"></div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className={`text-center transition-all duration-700 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="inline-flex items-center space-x-2 px-6 py-3 rounded-full bg-[#1a2b3c] hover:bg-[#243447] border border-amber-400/40 hover:border-amber-400/70 text-white font-medium text-sm tracking-wide transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            <span>Get a Free Assessment</span>
            <i className="ri-arrow-right-line text-base"></i>
          </button>
          <p className="text-sm text-gray-500 font-light mt-4">No commitment. No sales pressure. Just clarity on your property&apos;s potential.</p>
        </div>
      </div>
    </section>
  );
}
